// Pure scenario helpers for Forecast Studio and the Scenarios page:
// what-if phrase parsing, server driver-preview reconciliation, request
// nonces, scenario chip metadata, and per-scenario trend bundles. No React,
// no network — everything here is unit-testable.
import type {
  QuickBooksFpaForecastLineMetadata,
  QuickBooksFpaForecastPackage,
  QuickBooksFpaMlDriverPreviewLine,
  QuickBooksFpaStatementRender,
} from '../../../data/ServerApiClient'
import { formatAsOf, formatSignedCurrency, trimStr } from './cfoFormat'
import type { ScenarioSeries } from './charts'

export const FORECAST_SCENARIO_KEYS = ['base', 'upside', 'downside'] as const
export type ForecastScenarioKey = (typeof FORECAST_SCENARIO_KEYS)[number]

// ---------------------------------------------------------------------------
// What-if phrase parsing — small deterministic patterns, no AI calls.
// ---------------------------------------------------------------------------

export type WhatIfParseResult = {
  /** Driver key → numeric value in driver units (ratios for *_pct, days for DSO/DPO). */
  assumptions: Record<string, number>
}

const firstFiniteMatch = (input: string, patterns: RegExp[]): number | null => {
  for (const pattern of patterns) {
    const match = pattern.exec(input)
    if (!match) continue
    const value = Number(match[1])
    if (Number.isFinite(value)) return value
  }
  return null
}

const inRange = (value: number | null, min: number, max: number): number | null =>
  value != null && value >= min && value <= max ? value : null

/**
 * Parse simple driver phrases like "growth at 20%", "gross margin 55%",
 * "dso 45", "collect in 45 days", "pay vendors in 60 days", or
 * "opex 30% of sales" into driver-override values. Multiple clauses combine
 * ("growth 10% and dso 40"). Returns null when nothing parseable (or nothing
 * within sane driver ranges) is found.
 */
export const parseWhatIfPrompt = (text: string): WhatIfParseResult | null => {
  const input = String(text || '').toLowerCase()
  if (!input.trim()) return null
  const assumptions: Record<string, number> = {}
  const growthPct = inRange(firstFiniteMatch(input, [
    /(?:sales\s+|revenue\s+)?growth\s+(?:at|is|of|to|hits|=)?\s*(-?\d+(?:\.\d+)?)\s*%/,
    /(?:sales|revenue)\s+grows?\s+(?:by|at|to)?\s*(-?\d+(?:\.\d+)?)\s*%/,
  ]), -95, 500)
  if (growthPct != null) assumptions.sales_growth_pct = growthPct / 100
  const marginPct = inRange(firstFiniteMatch(input, [
    /(?:gross\s+)?margins?\s+(?:at|is|of|to|=)?\s*(\d+(?:\.\d+)?)\s*%/,
  ]), 0, 100)
  if (marginPct != null) assumptions.gross_margin_pct = marginPct / 100
  const dsoDays = inRange(firstFiniteMatch(input, [
    /\bdso\s+(?:at|is|of|to|=)?\s*(\d+(?:\.\d+)?)/,
    /collect(?:ions?|ing)?\s+(?:cash\s+)?in\s+(\d+(?:\.\d+)?)\s*days?/,
    /customers?\s+pay(?:ing)?\s+in\s+(\d+(?:\.\d+)?)\s*days?/,
  ]), 0, 365)
  if (dsoDays != null) assumptions.dso_days = dsoDays
  const dpoDays = inRange(firstFiniteMatch(input, [
    /\bdpo\s+(?:at|is|of|to|=)?\s*(\d+(?:\.\d+)?)/,
    /pay\s+(?:vendors?|suppliers?|bills?)\s+in\s+(\d+(?:\.\d+)?)\s*days?/,
  ]), 0, 365)
  if (dpoDays != null) assumptions.dpo_days = dpoDays
  const opexPct = inRange(firstFiniteMatch(input, [
    /\bopex\s+(?:at|is|of|to|=)?\s*(\d+(?:\.\d+)?)\s*%(?:\s*of\s+sales)?/,
    /operating\s+expenses?\s+(?:at|is|of|to|=)?\s*(\d+(?:\.\d+)?)\s*%/,
  ]), 0, 200)
  if (opexPct != null) assumptions.opex_pct = opexPct / 100
  return Object.keys(assumptions).length ? { assumptions } : null
}

const whatIfNumber = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)))

/** Human chip label for parsed what-if assumptions, e.g. "growth 20% · DSO 45 days". */
export const describeWhatIfAssumptions = (assumptions: Record<string, number>): string => {
  const parts: string[] = []
  if (assumptions.sales_growth_pct != null) parts.push(`growth ${whatIfNumber(assumptions.sales_growth_pct * 100)}%`)
  if (assumptions.gross_margin_pct != null) parts.push(`gross margin ${whatIfNumber(assumptions.gross_margin_pct * 100)}%`)
  if (assumptions.dso_days != null) parts.push(`DSO ${whatIfNumber(assumptions.dso_days)} days`)
  if (assumptions.dpo_days != null) parts.push(`DPO ${whatIfNumber(assumptions.dpo_days)} days`)
  if (assumptions.opex_pct != null) parts.push(`opex ${whatIfNumber(assumptions.opex_pct * 100)}% of sales`)
  return parts.join(' · ')
}

// ---------------------------------------------------------------------------
// Request nonce — guards async responses against out-of-order arrival.
// ---------------------------------------------------------------------------

export type RequestNonce = {
  next: () => number
  isCurrent: (nonce: number) => boolean
  current: () => number
}

export const createRequestNonce = (): RequestNonce => {
  let current = 0
  return {
    next: () => {
      current += 1
      return current
    },
    isCurrent: (nonce: number) => nonce === current,
    current: () => current,
  }
}

// ---------------------------------------------------------------------------
// Server driver-preview reconciliation: replace client-estimated forecast
// cells with the engine-exact values returned by the preview endpoint.
// ---------------------------------------------------------------------------

export type ForecastDriverPreviewApplication = {
  statement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null
  /** Forecast cells the preview matched (whether or not the value moved). */
  matchedCellCount: number
}

/**
 * Match preview lines to statement rows by node_id + period_start (direct-cash
 * leaf rows carry the original node id as a `:source:` suffix) and replace the
 * client-estimated values on forecast-period cells. Lines for other scenarios
 * or statement types are ignored; actual-period cells are never touched.
 */
export const applyServerDriverPreviewToStatement = (
  statement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null,
  lines: QuickBooksFpaMlDriverPreviewLine[] | null | undefined,
  scenarioKey: string,
): ForecastDriverPreviewApplication => {
  if (!statement || !lines?.length) return { statement, matchedCellCount: 0 }
  const wantedScenario = trimStr(scenarioKey) || 'base'
  const valuesByNodePeriod = new Map<string, number>()
  for (const line of lines) {
    if ((trimStr(line.scenario_key) || 'base') !== wantedScenario) continue
    if (trimStr(line.statement_type) !== trimStr(statement.statement_type)) continue
    const period = String(line.period_start || '').slice(0, 10)
    const nodeId = trimStr(line.node_id)
    const expected = Number(line.expected)
    if (!period || !nodeId || !Number.isFinite(expected)) continue
    valuesByNodePeriod.set(`${nodeId}|${period}`, expected)
  }
  if (!valuesByNodePeriod.size) return { statement, matchedCellCount: 0 }
  const periodKinds = statement.period_kinds || statement.periods.map(() => 'forecast')
  const periodKeys = statement.periods.map((period) => String(period || '').slice(0, 10))
  let matchedCellCount = 0
  let mutated = false
  const rows = statement.rows.map((row) => {
    const sourceNodeId = /:source:(.+)$/.exec(row.node_id || '')?.[1] || row.node_id
    let nextValues: Array<number | null> | null = null
    periodKeys.forEach((period, index) => {
      if (periodKinds[index] !== 'forecast') return
      const refined = valuesByNodePeriod.get(`${sourceNodeId}|${period}`)
      if (refined == null) return
      matchedCellCount += 1
      const current = Number(row.values?.[index])
      if (Number.isFinite(current) && Math.abs(current - refined) <= 0.005) return
      if (!nextValues) nextValues = [...row.values]
      nextValues[index] = refined
    })
    if (!nextValues) return row
    mutated = true
    return { ...row, values: nextValues }
  })
  if (!matchedCellCount) return { statement, matchedCellCount: 0 }
  return { statement: mutated ? { ...statement, rows } : statement, matchedCellCount }
}

// ---------------------------------------------------------------------------
// Scenario chip metadata from the package's validation_json.
// ---------------------------------------------------------------------------

export type ForecastScenarioMultipliers = {
  revenue_multiplier?: number | null
  expense_multiplier?: number | null
}

export type ForecastScenarioBandMeta = {
  basis: string
  upside?: ForecastScenarioMultipliers | null
  downside?: ForecastScenarioMultipliers | null
}

/** Engine-v2 packages publish `validation_json.scenarios` with `basis: 'driver_band_v2'`. */
export const parseScenarioBandMeta = (validationJson: any): ForecastScenarioBandMeta | null => {
  const scenarios = validationJson?.scenarios
  if (!scenarios || trimStr(scenarios.basis) !== 'driver_band_v2') return null
  return {
    basis: 'driver_band_v2',
    upside: scenarios.upside ?? null,
    downside: scenarios.downside ?? null,
  }
}

const multiplierPctLabel = (multiplier: unknown): string | null => {
  const value = Number(multiplier)
  if (!Number.isFinite(value) || value <= 0) return null
  const pct = (value - 1) * 100
  if (Math.abs(pct) < 0.05) return null
  const rounded = Math.abs(pct) >= 10 ? Math.round(pct) : Number(pct.toFixed(1))
  return `${rounded >= 0 ? '+' : ''}${rounded}%`
}

/** "Upside · revenue +9%" when multiplier metadata exists; plain name otherwise. */
export const scenarioChipLabel = (key: string, meta: ForecastScenarioBandMeta | null): string => {
  const name = key === 'base' ? 'Base' : key === 'upside' ? 'Upside' : key === 'downside' ? 'Downside' : key
  if (key === 'base' || !meta) return name
  const multipliers = key === 'upside' ? meta.upside : key === 'downside' ? meta.downside : null
  const pct = multiplierPctLabel(multipliers?.revenue_multiplier)
  return pct ? `${name} · revenue ${pct}` : name
}

/** Scenario keys the package actually published (summary_json.scenario_keys), base-first. */
export const packageScenarioKeys = (
  pkg: Pick<QuickBooksFpaForecastPackage, 'summary_json'> | null | undefined,
): string[] => {
  const raw = pkg?.summary_json?.scenario_keys
  if (Array.isArray(raw)) {
    const keys = raw.map((key) => trimStr(key as any)).filter(Boolean)
    if (keys.length) return keys.includes('base') ? keys : ['base', ...keys]
  }
  return ['base']
}

// ---------------------------------------------------------------------------
// Scenario trend bundles for the Scenarios page.
// ---------------------------------------------------------------------------

export type ScenarioBundle = {
  series: ScenarioSeries[]
  axisLabels: string[]
  baseEnd: number | null
  upsideEnd: number | null
  downsideEnd: number | null
  /** Real per-scenario forecast lines vs the base line's confidence band. */
  source: 'scenario_lines' | 'confidence_band'
}

const dominantProfitAndLossNode = (lines: QuickBooksFpaForecastLineMetadata[]): string => {
  const byNode = new Map<string, number>()
  for (const line of lines) {
    const value = Math.abs(line.final_value ?? line.generated_value ?? 0)
    byNode.set(line.node_id, (byNode.get(line.node_id) || 0) + value)
  }
  let dominant = ''
  let best = -1
  for (const [node, total] of byNode) {
    if (total > best) {
      best = total
      dominant = node
    }
  }
  return dominant
}

const scenarioAxisLabels = (periods: string[]): string[] =>
  [periods[0], periods[Math.floor(periods.length / 2)], periods[periods.length - 1]].map((period) => formatAsOf(period))

const cumulativePathForPeriods = (
  lines: QuickBooksFpaForecastLineMetadata[] | null | undefined,
  nodeId: string,
  periods: string[],
): { points: number[]; end: number } | null => {
  const byPeriod = new Map<string, number>()
  for (const line of lines || []) {
    if (line.statement_type !== 'profit_and_loss' || line.node_id !== nodeId) continue
    const value = line.final_value ?? line.generated_value
    if (value == null || !Number.isFinite(Number(value))) continue
    byPeriod.set(String(line.period_start || '').slice(0, 10), Number(value))
  }
  let running = 0
  const points: number[] = []
  for (const period of periods) {
    const value = byPeriod.get(period)
    // A scenario missing any base period means its path is not comparable —
    // bail so the caller falls back to the honest confidence-band fan.
    if (value == null) return null
    running += value
    points.push(running)
  }
  return points.length >= 2 ? { points, end: running } : null
}

/**
 * Real per-scenario cumulative paths from the package's scenario forecast
 * lines (base / upside / downside all generated by the engine). Returns null
 * when any scenario lacks lines for the base periods, so callers can fall
 * back to the band-derived fan instead of fabricating one.
 */
export const scenarioBundleFromScenarioLines = (
  linesByScenario: Partial<Record<ForecastScenarioKey, QuickBooksFpaForecastLineMetadata[]>>,
): ScenarioBundle | null => {
  const baseLines = (linesByScenario.base || []).filter((line) => line.statement_type === 'profit_and_loss')
  if (!baseLines.length) return null
  const dominant = dominantProfitAndLossNode(baseLines)
  if (!dominant) return null
  const periods = [...new Set(
    baseLines
      .filter((line) => line.node_id === dominant)
      .map((line) => String(line.period_start || '').slice(0, 10))
      .filter(Boolean),
  )].sort()
  if (periods.length < 2) return null
  const base = cumulativePathForPeriods(baseLines, dominant, periods)
  const upside = cumulativePathForPeriods(linesByScenario.upside, dominant, periods)
  const downside = cumulativePathForPeriods(linesByScenario.downside, dominant, periods)
  if (!base || !upside || !downside) return null
  const series: ScenarioSeries[] = [
    { key: 'upside', label: 'Upside', points: upside.points, tone: 'upside', endDisplay: formatSignedCurrency(upside.end) },
    { key: 'base', label: 'Base', points: base.points, tone: 'base', endDisplay: formatSignedCurrency(base.end) },
    { key: 'downside', label: 'Downside', points: downside.points, tone: 'downside', endDisplay: formatSignedCurrency(downside.end) },
  ]
  return {
    series,
    axisLabels: scenarioAxisLabels(periods),
    baseEnd: base.end,
    upsideEnd: upside.end,
    downsideEnd: downside.end,
    source: 'scenario_lines',
  }
}

/**
 * Fallback for packages without per-scenario rows: derive base / upside /
 * downside cumulative paths from the dominant P&L node's confidence band
 * (final / high / low). The fan IS the model's own interval — base is the
 * projection, upside the high band, downside the low band — not a fabricated
 * scenario, and the UI labels it as band-derived.
 */
export const scenarioBundleFromConfidenceBand = (
  lines: QuickBooksFpaForecastLineMetadata[] | null | undefined,
): ScenarioBundle | null => {
  const profitAndLossLines = (lines || []).filter((line) => line.statement_type === 'profit_and_loss')
  if (!profitAndLossLines.length) return null
  const dominant = dominantProfitAndLossNode(profitAndLossLines)
  const points = profitAndLossLines
    .filter((line) => line.node_id === dominant)
    .sort((a, b) => a.period_start.localeCompare(b.period_start))
  if (points.length < 2) return null
  let base = 0
  let up = 0
  let down = 0
  const basePts: number[] = []
  const upPts: number[] = []
  const downPts: number[] = []
  for (const point of points) {
    const mid = point.final_value ?? point.generated_value ?? 0
    base += mid
    up += point.high_value ?? mid
    down += point.low_value ?? mid
    basePts.push(base)
    upPts.push(up)
    downPts.push(down)
  }
  const series: ScenarioSeries[] = [
    { key: 'upside', label: 'Upside', points: upPts, tone: 'upside', endDisplay: formatSignedCurrency(up) },
    { key: 'base', label: 'Base', points: basePts, tone: 'base', endDisplay: formatSignedCurrency(base) },
    { key: 'downside', label: 'Downside', points: downPts, tone: 'downside', endDisplay: formatSignedCurrency(down) },
  ]
  return {
    series,
    axisLabels: scenarioAxisLabels(points.map((point) => point.period_start)),
    baseEnd: base,
    upsideEnd: up,
    downsideEnd: down,
    source: 'confidence_band',
  }
}
